import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { createCheckout, getKingsCircle } from "../lib/api";
import { Crown, ShieldCheck, Sparkles, Loader2, Check } from "lucide-react";

const TIERS = [
  { key: "kings_ally", name: "Ally", amount: 25,
    perks: ["Monthly impact report", "Kings' Circle updates from Coach Kaf", "Public wall recognition (optional)"] },
  { key: "kings_sponsor", name: "Sponsor", amount: 50, featured: true,
    perks: ["Everything in Ally", "Signed thank-you from a mentee twice a year", "Invitation to the Annual Rising Kings Gala"] },
  { key: "kings_founding", name: "Founding Member", amount: 100,
    perks: ["Everything in Sponsor", "Founding Member plaque on our wall", "Two seats at every quarterly community event"] },
];

export default function KingsCircle() {
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [publicWall, setPublicWall] = useState(true);
  const [loadingKey, setLoadingKey] = useState(null);

  const { data: wall = [] } = useQuery({ queryKey: ["kings-circle"], queryFn: getKingsCircle });

  const join = async (tier) => {
    setLoadingKey(tier.key);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: tier.key, amount: tier.amount,
        origin_url: window.location.origin,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        recurring: true, display_public: publicWall,
      });
      window.location.href = checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Try again in a moment.");
      setLoadingKey(null);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="noise" />
        <div className="container-x relative py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">The Kings' Circle</span>
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-bold tracking-tight leading-none">
              Fund the future.<br /><span className="italic text-[#F5B90A]">Monthly.</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-lg">
              Recurring gifts keep the brotherhood alive between fundraisers. Join a circle of donors who believe in these young men every single month — and get named on our public wall of Kings.
            </p>
            <div className="mt-8 flex items-center gap-4 text-sm text-white/70">
              <ShieldCheck className="h-5 w-5 text-[#F5B90A]" /> 100% tax-deductible · Cancel anytime · Secure Stripe checkout
            </div>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end relative">
            <div className="absolute inset-6 bg-[#F5B90A]/15 rounded-full blur-3xl" />
            <Crown className="relative h-40 w-40 text-[#F5B90A]" strokeWidth={1.5} />
          </div>
        </div>
      </section>

      <section className="bg-[#FDFBF7] border-y border-black/10">
        <div className="container-x py-16 md:py-24">
          <div className="mb-10 max-w-3xl">
            <span className="overline text-[#6B21A8]">Pick your tier</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">Three ways to walk with us.</h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 max-w-xl mb-8">
            <input placeholder="Your name (optional)" value={donorName} onChange={(e) => setDonorName(e.target.value)} data-testid="kc-donor-name"
              className="rounded-md border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#6B21A8]" />
            <input type="email" placeholder="Email for receipt" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} data-testid="kc-donor-email"
              className="rounded-md border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#6B21A8]" />
          </div>
          <label className="flex items-center gap-3 mb-10 text-sm text-black/70">
            <input type="checkbox" checked={publicWall} onChange={(e) => setPublicWall(e.target.checked)} data-testid="kc-public"
              className="h-4 w-4 rounded border-black/30 text-[#6B21A8] focus:ring-[#6B21A8]" />
            List me on the public Kings' Circle wall (first name + last initial only)
          </label>

          <div className="grid gap-6 md:grid-cols-3" data-testid="kings-tiers">
            {TIERS.map((t) => (
              <div key={t.key} data-testid={`tier-${t.key}`}
                className={`relative p-8 flex flex-col ${t.featured ? "bg-[#0A0A0A] text-white border-2 border-[#F5B90A]" : "bg-white border border-black/10"}`}>
                {t.featured && (
                  <span className="absolute -top-3 left-6 bg-[#F5B90A] text-black text-[10px] font-bold uppercase tracking-widest px-3 py-1">Most joined</span>
                )}
                <p className={`overline ${t.featured ? "text-[#F5B90A]" : "text-[#6B21A8]"}`}>{t.name}</p>
                <p className="mt-4 font-display text-6xl font-bold leading-none">${t.amount}</p>
                <p className={`mt-2 text-sm ${t.featured ? "text-white/60" : "text-black/50"}`}>per month</p>
                <ul className={`mt-6 space-y-3 text-sm flex-1 ${t.featured ? "text-white/80" : "text-black/70"}`}>
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${t.featured ? "text-[#F5B90A]" : "text-[#6B21A8]"}`} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => join(t)} disabled={loadingKey !== null} data-testid={`tier-join-${t.key}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-md px-6 py-3 font-semibold transition-transform hover:-translate-y-0.5 ${
                    t.featured ? "bg-[#F5B90A] text-black" : "bg-[#6B21A8] text-white"
                  } disabled:opacity-50`}>
                  {loadingKey === t.key ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>) : (<><Sparkles className="mr-2 h-4 w-4" /> Join as {t.name}</>)}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-10 text-sm text-black/60">Prefer a one-time gift? <Link to="/donate" className="text-[#6B21A8] font-semibold underline">Give once instead →</Link></p>
        </div>
      </section>

      {/* Public wall */}
      <section className="bg-[#4A154B] text-white">
        <div className="container-x py-20 md:py-24">
          <span className="overline text-[#F5B90A]">Wall of Kings</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">The people funding the brotherhood.</h2>
          <p className="mt-4 max-w-2xl text-white/70">Every name below is a monthly donor showing up for young men in Clayton County. Add yours.</p>
          {wall.length === 0 ? (
            <p className="mt-10 text-white/60">Be the first name on this wall.</p>
          ) : (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="kings-wall">
              {wall.map((d, i) => (
                <div key={i} className="border border-white/15 p-4">
                  <p className="font-display text-xl font-semibold">{d.display_name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#F5B90A] mt-1">
                    {d.tier === "kings_founding" ? "Founding Member" : d.tier === "kings_sponsor" ? "Sponsor" : "Ally"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
